"use client";

import React from 'react';
import Link from 'next/link';
import { LandingHeader } from '@/app/components/LandingHeader';
import { Footer } from '@/app/components/Footer';

export default function PrivacyPolicyPage() {
  return (
    <div className="flex flex-col min-h-screen bg-background text-foreground font-body">
      <LandingHeader transparent />
      
      <main className="flex-1">
        <section className="pt-32 pb-20 px-6">
          <div className="container mx-auto max-w-4xl">
            <h1 className="font-display text-4xl md:text-6xl mb-4">Privacy Policy</h1>
            <p className="text-muted-foreground mb-12">Last updated: 19 December 2025</p>
            
            <div className="prose prose-slate max-w-none space-y-8 text-lg font-light leading-relaxed text-muted-foreground">
              <p>
                This Privacy Policy explains how Willdone (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;) collects and uses personal data when you use our website and services.
              </p>

              <section>
                <h2 className="text-2xl font-display text-foreground mb-4">1. Who we are (data controller)</h2>
                <p>
                  Willdone is an England and Wales will-writing website.
                </p>
                <p>
                  <strong>Contact email:</strong> hello@willdone.co.uk
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-display text-foreground mb-4">2. The personal data we collect</h2>
                <p>We may collect the following categories of personal data:</p>
                
                <h3 className="text-xl font-medium text-foreground mt-4 mb-2">2.1 Data you provide to us</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Account and contact details:</strong> name, email, phone number (if provided)</li>
                  <li><strong>Will questionnaire information:</strong> details you enter to generate your will (for example, your address, family relationships, executors, guardians, beneficiaries, gifts, and other instructions)</li>
                  <li><strong>Support communications:</strong> emails or messages you send us</li>
                </ul>

                <h3 className="text-xl font-medium text-foreground mt-6 mb-2">2.2 Data about other people you include</h3>
                <p>
                  When you enter details of executors, witnesses, beneficiaries, guardians, or other third parties, you may provide their names and contact details and their relationship to you. You should only provide this information if you have the right to do so, and you should consider telling them you&rsquo;ve included their details.
                </p>

                <h3 className="text-xl font-medium text-foreground mt-6 mb-2">2.3 Technical and usage data</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>IP address, device and browser information</li>
                  <li>pages viewed and actions taken on the website</li>
                  <li>cookies and similar technologies (see section 9)</li>
                </ul>

                <h3 className="text-xl font-medium text-foreground mt-6 mb-2">2.4 Payment data</h3>
                <p>
                  Payments are processed by Stripe. We do not receive or store your full card details. Stripe will process payment information as a separate controller/processor depending on the context. (You should also link to Stripe&rsquo;s privacy information from your checkout.)
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-display text-foreground mb-4">3. How we use your data and our legal bases</h2>
                <p>UK GDPR requires a &ldquo;lawful basis&rdquo; for processing. We typically use the following:</p>

                <h3 className="text-xl font-medium text-foreground mt-4 mb-2">3.1 To provide the service (Contract)</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>create and deliver your will documents and related outputs</li>
                  <li>provide customer support</li>
                  <li>manage your account (if applicable)</li>
                </ul>
                <p className="mt-2 italic">Lawful basis: performance of a contract (to provide what you asked for).</p>

                <h3 className="text-xl font-medium text-foreground mt-6 mb-2">3.2 To run and improve the website (Legitimate interests)</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>maintain, secure, and troubleshoot the website</li>
                  <li>prevent fraud and abuse</li>
                  <li>understand how the website is used and improve usability (where this relies on non-essential cookies/analytics, we will ask for consent first)</li>
                </ul>
                <p className="mt-2 italic">Lawful basis: legitimate interests (running a safe, reliable service), and/or consent for non-essential cookies.</p>

                <h3 className="text-xl font-medium text-foreground mt-6 mb-2">3.3 To comply with legal obligations (Legal obligation)</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>accounting and tax records (e.g., VAT records where applicable)</li>
                  <li>responding to lawful requests from authorities</li>
                </ul>
                <p className="mt-2 italic">Lawful basis: legal obligation.</p>

                <h3 className="text-xl font-medium text-foreground mt-6 mb-2">3.4 Marketing communications (Consent or legitimate interests)</h3>
                <p>
                  If you opt in, we may send updates and service information by email. You can unsubscribe at any time via the link in the email or by contacting us.
                </p>
                <p className="mt-2 italic">Lawful basis: consent (where required) or legitimate interests for limited service messages (e.g., transactional emails).</p>
              </section>

              <section>
                <h2 className="text-2xl font-display text-foreground mb-4">4. Who we share personal data with</h2>
                <p>We may share personal data with trusted service providers, for example:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Stripe (payment processing)</li>
                  <li>website hosting and infrastructure providers [add provider]</li>
                  <li>email or customer support providers [add provider]</li>
                  <li>analytics providers (only where you consent to non-essential cookies) [add provider if used]</li>
                </ul>
                <p className="mt-4">We share only what is necessary for them to provide their services. </p>
              </section>

              <section>
                <h2 className="text-2xl font-display text-foreground mb-4">5. International transfers</h2>
                <p>
                  Some of our service providers may process data outside the UK. Where this happens, we will use appropriate safeguards (for example, adequacy regulations or standard contractual clauses) as required.
                </p>
                <p className="mt-4 italic">
                  [If you know your hosting/email providers, add where they process data and what safeguard applies.]
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-display text-foreground mb-4">6. How long we keep your data (retention)</h2>
                <p>We keep personal data only for as long as necessary for the purposes described in this policy, including:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>while you have an account or active relationship with us</li>
                  <li>as needed to provide the service and handle support queries</li>
                  <li>to comply with legal, accounting, or tax obligations</li>
                  <li>to establish, exercise, or defend legal claims</li>
                </ul>
                <p className="mt-4 italic">Typical retention periods: [add your actual periods, e.g., &ldquo;account data retained for X years after last activity&rdquo;].</p>
              </section>

              <section>
                <h2 className="text-2xl font-display text-foreground mb-4">7. Your rights</h2>
                <p>You have rights under UK data protection law, including:</p>
                <ul className="list-disc pl-6 mb-4 space-y-2">
                  <li>access to your personal data</li>
                  <li>correction of inaccurate data</li>
                  <li>deletion (in some cases)</li>
                  <li>restriction of processing (in some cases)</li>
                  <li>objection to processing based on legitimate interests</li>
                  <li>data portability (in some cases)</li>
                  <li>withdraw consent at any time (where we rely on consent)</li>
                </ul>
                <p>You can exercise these rights by contacting hello@willdone.co.uk.</p>
              </section>

              <section>
                <h2 className="text-2xl font-display text-foreground mb-4">8. Complaints</h2>
                <p>
                  If you&rsquo;re unhappy with how we handle your data, please contact us first at hello@willdone.co.uk.
                </p>
                <p className="mt-4">
                  You also have the right to complain to the Information Commissioner&rsquo;s Office (ICO) (the UK regulator for data protection).
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-display text-foreground mb-4">9. Cookies and similar technologies</h2>
                <p>
                  We use cookies and similar technologies. Some are essential for the site to work; others (like analytics) require your consent. UK rules apply to cookies even where data is &ldquo;anonymous,&rdquo; and non-essential cookies generally require consent.
                </p>
                <p className="mt-4">
                  See our <Link href="/cookie-policy" className="text-primary hover:underline underline-offset-4 font-medium">Cookie Policy</Link>.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-display text-foreground mb-4">10. Security</h2>
                <p>
                  We use reasonable technical and organisational measures to protect personal data. However, no method of transmission or storage is completely secure.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-display text-foreground mb-4">11. Children&rsquo;s privacy</h2>
                <p>
                  Our services are intended for people aged 18 and over. We do not knowingly collect data from children.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-display text-foreground mb-4">12. Changes to this policy</h2>
                <p>
                  We may update this Privacy Policy from time to time. We will post the updated version on the website and update the effective date.
                </p>
              </section>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}



